import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Globe, Info, Loader2, AlertCircle, RotateCcw, Clock, GraduationCap, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { type ScanResult } from "@/lib/api/ai-visibility";
import { type PlatformVisibilityResult } from "@/lib/api/platform-visibility";
import { scanGoogleSearchVisibility, type GoogleSearchResult } from "@/lib/api/google-search-visibility";
import ScanProgress from "./ScanProgress";
import { trackScanStarted, trackScanFailed } from "@/lib/analytics";
import { isDebugMode } from "@/lib/debug";
import { supabase } from "@/integrations/supabase/client";
import { 
  createMockAIResult, 
  createMockPlatformResult, 
  createMockGoogleResult, 
  MOCK_DELAY_MS 
} from "@/lib/mock/scanMockData";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Timeout duration in milliseconds (60 seconds - accounts for Discovery + Fallback queries)
const SCAN_TIMEOUT_MS = 60000;

// Discord webhook notification via secure edge function
const notifyDiscordScan = async (data: {
  brand: string;
  keyword: string;
  url: string;
}) => {
  // Skip if debug mode is active (testing)
  if (isDebugMode()) {
    return;
  }

  try {
    const { error } = await supabase.functions.invoke('notify-discord-scan', {
      body: data,
    });

    if (error) {
      console.error('[Discord] Edge function error:', error);
      return;
    }
  } catch (error) {
    console.error('[Discord] Webhook error:', error);
    // Don't block the scan if webhook fails
  }
};

// Wrap a promise with a timeout
const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    ),
  ]);
};

// Classify error type for analytics
const classifyError = (error: unknown): 'timeout' | 'network' | 'api_error' | 'unknown' => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'timeout';
    }
    if (message.includes('network') || message.includes('fetch') || message.includes('failed to fetch')) {
      return 'network';
    }
    if (message.includes('rate limit') || message.includes('api') || message.includes('500') || message.includes('502') || message.includes('503')) {
      return 'api_error';
    }
  }
  return 'unknown';
};

interface ScanFormProps {
  onScanComplete?: (
    result: ScanResult, 
    platformResult?: PlatformVisibilityResult,
    googleResult?: GoogleSearchResult,
    websiteUrl?: string
  ) => void;
  onScanStart?: () => void;
}

const MAINTENANCE_MODE = false;

const ScanForm = ({ onScanComplete, onScanStart }: ScanFormProps) => {
  const [brandName, setBrandName] = useState("");
  const [targetKeyword, setTargetKeyword] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [scanError, setScanError] = useState<{ message: string; type: 'timeout' | 'network' | 'api_error' | 'unknown' } | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [urlError, setUrlError] = useState("");
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  if (MAINTENANCE_MODE) {
    return (
      <div className="w-full space-y-4 text-center py-6">
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <Wrench className="w-7 h-7 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            AILens scanning is currently paused
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This is a completed research project (Jan-Feb 2026). The scanner may be re-enabled in the future.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Questions?{" "}
          <a href="mailto:hello@ai-lens.app" className="underline hover:text-foreground transition-colors">
            Get in touch
          </a>
        </p>
      </div>
    );
  }

  // Normalize URL by adding https:// if no protocol is present
  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return "";
    // If URL doesn't start with http:// or https://, prepend https://
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  // Valid TLDs list (top common ones)
  const validTLDs = [
    // Generic
    'com', 'org', 'net', 'io', 'co', 'app', 'dev',
    // Tech
    'ai', 'tech', 'digital', 'online', 'cloud',
    // Business
    'biz', 'company', 'agency', 'solutions',
    // Country codes (popular)
    'uk', 'us', 'ca', 'au', 'de', 'fr', 'nl', 'il', 'eu', 'in', 'jp', 'br', 'mx', 'es', 'it',
    // New TLDs
    'blog', 'shop', 'store', 'design', 'marketing', 'media', 'studio', 'consulting', 'services'
  ];

  const validateUrl = (url: string): { valid: boolean; error: string } => {
    // Remove whitespace
    const trimmed = url.trim();
    
    if (!trimmed) {
      return { valid: false, error: "Please enter your website URL" };
    }
    
    // Remove protocol if present for checking
    let cleanUrl = trimmed.replace(/^https?:\/\//, '').toLowerCase();
    // Remove www. if present
    cleanUrl = cleanUrl.replace(/^www\./, '');
    // Remove trailing slash
    cleanUrl = cleanUrl.replace(/\/$/, '');
    
    // Check if it contains at least one dot
    if (!cleanUrl.includes('.')) {
      return { valid: false, error: "Please enter a valid domain (e.g., example.com)" };
    }
    
    // Split by dots to get parts
    const parts = cleanUrl.split('.');
    
    // Must have at least 2 parts (domain.tld)
    if (parts.length < 2) {
      return { valid: false, error: "Please enter a complete domain (e.g., example.com)" };
    }
    
    // Get TLD (last part after final dot)
    let tld = parts[parts.length - 1];
    // Remove any path/query params from TLD
    tld = tld.split('/')[0].split('?')[0];
    
    // Check if TLD is empty (e.g., "example.")
    if (!tld) {
      return { valid: false, error: "Please enter a complete domain (e.g., example.com)" };
    }
    
    // Check if TLD is valid
    if (!validTLDs.includes(tld)) {
      // Allow any alphabetic TLD with 2-6 characters as fallback
      if (tld.length < 2 || tld.length > 6 || !/^[a-z]+$/.test(tld)) {
        return { 
          valid: false, 
          error: `"${tld}" is not a valid domain extension. Did you mean .com?`
        };
      }
    }
    
    // Normalize before final URL validation
    const normalized = normalizeUrl(trimmed);
    
    try {
      const parsed = new URL(normalized);
      
      // Block localhost
      if (parsed.hostname === 'localhost') {
        return { valid: false, error: "Please enter a valid website URL (e.g., example.com)" };
      }
      
      return { valid: true, error: "" };
    } catch {
      return { valid: false, error: "Please enter a valid website URL (e.g., example.com)" };
    }
  };

  const handleUrlBlur = () => {
    const trimmed = websiteUrl.trim();
    if (trimmed) {
      const { valid, error } = validateUrl(trimmed);
      setUrlError(valid ? "" : error);
    } else {
      setUrlError("");
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebsiteUrl(e.target.value);
    // Clear error when user starts typing again
    if (urlError) {
      setUrlError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // VERIFICATION LOGGING - DO NOT REMOVE
    const debugCheck = isDebugMode();
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔍 DEBUG MODE CHECK:');
    console.log('  isDebugMode():', debugCheck);
    console.log('  URL:', window.location.href);
    console.log('  Has ?debug=true:', window.location.search.includes('debug=true'));
    console.log('  Has ?test=true:', window.location.search.includes('test=true'));
    console.log('  sessionStorage:', sessionStorage.getItem('debug_mode_session'));
    console.log('  localStorage:', localStorage.getItem('debug_mode'));
    console.log('  hostname:', window.location.hostname);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    if (debugCheck) {
      console.log('✅ DEBUG MODE ACTIVE - SHOULD USE MOCK DATA');
    } else {
      console.log('❌ DEBUG MODE INACTIVE - WILL CALL REAL APIs');
    }
    
    // Clear any previous scan error
    setScanError(null);
    
    // Check honeypot - if filled, it's a bot
    if (honeypot) {
      // Silently fail for bots - don't give them feedback
      toast({
        title: "Scan Complete",
        description: "Your scan is being processed",
      });
      return;
    }
    
    if (!brandName.trim() || !targetKeyword.trim()) {
      toast({
        title: "Missing fields",
        description: "Please enter both brand name and target keyword",
        variant: "destructive",
      });
      return;
    }

    // Validate URL - now required for accurate results
    const trimmedUrl = websiteUrl.trim();
    if (!trimmedUrl) {
      setUrlError("Please enter your website URL for accurate Google ranking results");
      toast({
        title: "Website required",
        description: "Please enter your website URL for accurate Google ranking results",
        variant: "destructive",
      });
      return;
    }
    
    const urlValidation = validateUrl(trimmedUrl);
    if (!urlValidation.valid) {
      setUrlError(urlValidation.error);
      toast({
        title: "Invalid URL",
        description: urlValidation.error,
        variant: "destructive",
      });
      return;
    }
    
    // Clear any URL error on successful validation
    setUrlError("");
    
    // Normalize URL for API calls (prepend https:// if missing)
    const normalizedUrl = normalizeUrl(trimmedUrl);

    // Debug/mock mode short-circuit BEFORE any scan API calls
    if (debugCheck) {
      setIsLoading(true);
      onScanStart?.();

      console.log('🐛 [DEBUG MODE] Returning mock data - no API calls made');

      // Simulate loading delay for realistic UX testing
      await new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));

      // Create mock results with user's actual input
      const mockVisibility = createMockAIResult(brandName.trim(), targetKeyword.trim());
      const mockPlatform = createMockPlatformResult(brandName.trim(), targetKeyword.trim());
      const mockGoogle = createMockGoogleResult(brandName.trim(), targetKeyword.trim());

      setIsLoading(false);
      
      // Toast AFTER loading state is cleared
      setTimeout(() => {
        toast({
          title: "Debug Mode - Mock Scan Complete!",
          description: "Using mock data to save API credits",
        });
      }, 100);

      onScanComplete?.(mockVisibility, mockPlatform, mockGoogle, normalizedUrl || undefined);
      return;
    }

    setIsLoading(true);
    onScanStart?.();
    
    // Track scan started event
    trackScanStarted(brandName.trim(), targetKeyword.trim(), normalizedUrl || undefined);
    
    // Notify Discord (non-blocking)
    notifyDiscordScan({
      brand: brandName.trim(),
      keyword: targetKeyword.trim(),
      url: normalizedUrl,
    });
    
    try {
      // V2.1: Use unified scan API for AI + Platform results (50% cost savings)
      // Google search runs separately as it uses SerpAPI
      const [unifiedSettled, googleSettled] = await Promise.allSettled([
        withTimeout(
          supabase.functions.invoke('unified-ai-visibility-scan', {
            body: { brandName: brandName.trim(), targetKeyword: targetKeyword.trim() }
          }),
          SCAN_TIMEOUT_MS,
          'AI visibility scan timed out. Please try again.'
        ),
        withTimeout(
          scanGoogleSearchVisibility(brandName.trim(), targetKeyword.trim(), normalizedUrl),
          SCAN_TIMEOUT_MS,
          'Google search scan timed out.'
        ),
      ]);

      if (unifiedSettled.status === "rejected") {
        throw unifiedSettled.reason;
      }

      const unifiedResponse = unifiedSettled.value;
      if (unifiedResponse.error) {
        throw new Error(unifiedResponse.error.message || 'Unified scan failed');
      }
      
      const data = unifiedResponse.data;
      
      // Map unified response to existing ScanResult structure with V2.1 fields
      const visibilityResult: ScanResult = {
        success: true,
        brandName: data.brandName,
        targetKeyword: data.targetKeyword,
        analysis: {
          visibilityScore: data.overallAnalysis?.visibilityScore || 0,
          keyFindings: data.overallAnalysis?.keyFindings || [],
          competitorMentions: data.overallAnalysis?.competitorMentions || [],
          recommendations: data.overallAnalysis?.recommendations || [],
          summary: data.overallAnalysis?.summary || '',
        },
        // V2.1 Discovery Mode fields
        aiScore: data.aiScore,
        queryType: data.queryType,
        queryUsed: data.queryUsed,
        rawResponseSnippet: data.rawResponseSnippet,
        competitorMentions: data.competitorMentions,
        // Shareable URL
        scan_id: data.scan_id,
      };
      
      // Map unified response to platform result structure
      const platformResult: PlatformVisibilityResult = {
        success: true,
        brandName: data.brandName,
        targetKeyword: data.targetKeyword,
        platforms: data.platformAnalysis?.platforms || [],
        summary: data.platformAnalysis?.summary || '',
      };

      const googleResult = googleSettled.status === "fulfilled" ? googleSettled.value : undefined;

      // Patch Google result into saved scan (non-blocking, service_role only)
      if (visibilityResult.scan_id && googleResult) {
        supabase.functions.invoke('update-scan-google', {
          body: {
            scan_id: visibilityResult.scan_id,
            googleResult: { ...googleResult, websiteUrl: normalizedUrl },
          },
        }).catch(err => console.error('Failed to save Google result:', err));
      }

      // Surface partial failures as non-blocking toasts
      const googleError = googleSettled.status === "rejected" ? googleSettled.reason : null;
      const errorToMessage = (err: unknown) => (err instanceof Error ? err.message : String(err));

      if (googleError) {
        const errorMsg = errorToMessage(googleError);
        if (errorMsg.toLowerCase().includes("rate limit")) {
          toast({
            title: "Some sources are temporarily limited",
            description: "You're scanning frequently. One or more sources hit the hourly limit. Your main score is still shown.",
          });
        }
      }

      // Clear loading state
      setIsLoading(false);
      
      // Toast moved to ResultsPage to fire after data is visible
      onScanComplete?.(visibilityResult, platformResult, googleResult, normalizedUrl || undefined);
    } catch (error) {
      console.error("Scan error:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      const errorType = classifyError(error);
      
      // Track failed scan in analytics
      trackScanFailed(
        brandName.trim(),
        targetKeyword.trim(),
        errorType,
        errorMessage,
        normalizedUrl || undefined
      );
      
      // Set error state for UI display
      let userFriendlyMessage = errorMessage;
      if (errorType === 'timeout') {
        userFriendlyMessage = "The scan took too long to complete. This can happen during high traffic periods.";
      } else if (errorType === 'network') {
        userFriendlyMessage = "Unable to connect to our servers. Please check your internet connection.";
      } else if (errorMessage.toLowerCase().includes("rate limit")) {
        userFriendlyMessage = "You've reached the scan limit. Please try again in an hour.";
      }
      
      setScanError({ message: userFriendlyMessage, type: errorType });
      
      toast({
        title: "Scan Failed",
        description: userFriendlyMessage,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setScanError(null);
    // Trigger form submission programmatically
    formRef.current?.requestSubmit();
  };

  // Error recovery UI
  if (scanError && !isLoading) {
    return (
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-lg text-foreground">Scan Failed</h3>
            <p className="text-sm text-muted-foreground">
              {scanError.message}
            </p>
          </div>
          <div className="pt-2 space-y-3">
            <Button 
              onClick={handleRetry}
              className="w-full h-11 font-semibold"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
            <button
              onClick={() => setScanError(null)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Edit scan details
            </button>
          </div>
        </div>
        
        {/* Show preserved form data */}
        <div className="bg-secondary/50 border border-border/50 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Brand:</span>
            <span className="font-medium text-foreground">{brandName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Keyword:</span>
            <span className="font-medium text-foreground">{targetKeyword}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Website:</span>
            <span className="font-medium text-foreground truncate ml-2">{websiteUrl}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="w-full max-w-md space-y-4">
      {isLoading ? (
        <ScanProgress isScanning={isLoading} />
      ) : (
        <>
          <div className="space-y-3">
            <Input
              type="text"
              placeholder="Brand Name"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              className="h-12 bg-secondary border-border/50 placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20 transition-all duration-300 focus:shadow-glow-sm"
              maxLength={100}
              required
            />
            
            {/* Target Keyword with info popover */}
            <div className="relative">
              <Input
                type="text"
                placeholder="Target Keyword"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
                className="h-12 pr-10 bg-secondary border-border/50 placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20 transition-all duration-300 focus:shadow-glow-sm"
                maxLength={100}
                required
              />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors touch-manipulation p-1 rounded-full hover:bg-secondary"
                    aria-label="What is target keyword?"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent 
                  side="right" 
                  align="center"
                  sideOffset={12}
                  className="w-auto max-w-[220px] px-4 py-3 text-xs bg-white dark:bg-card border-0 rounded-lg shadow-[2px_2px_8px_rgba(0,0,0,0.25)] z-[9999]"
                >
                  <p className="text-foreground leading-relaxed m-0">
                    The contextual keyword to audit your brand visibility.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Website URL field */}
            <div>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="yoursite.com"
                  value={websiteUrl}
                  onChange={handleUrlChange}
                  onBlur={handleUrlBlur}
                  className={cn(
                    "h-12 pl-10 bg-secondary border-border/50 placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20 transition-all duration-300 focus:shadow-glow-sm",
                    urlError && "border-destructive focus:border-destructive focus:ring-destructive/20"
                  )}
                  maxLength={500}
                  required
                />
              </div>
              {urlError && (
                <p className="mt-1.5 text-xs text-destructive pl-1">
                  {urlError}
                </p>
              )}
            </div>

            {/* Honeypot field - hidden from users, visible to bots */}
            <input
              type="text"
              name="website_check"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="absolute -left-[9999px] opacity-0 pointer-events-none"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
          </div>
          
          <Button
            type="submit" 
            size="lg" 
            disabled={isLoading}
            className={cn(
              "w-full h-12 text-base font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all duration-300",
              isLoading
                ? "scan-shimmer text-primary-foreground"
                : "animate-pulse-button"
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Scan Now
              </>
            )}
           </Button>

          {/* Trust signal - single clean line */}
          <div className="flex justify-center mt-3">
            <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
              Free ·{" "}
              <a href="/methodology" className="inline-flex items-center gap-1 text-muted-foreground underline decoration-dotted underline-offset-[3px] hover:decoration-solid">
                <GraduationCap className="h-3.5 w-3.5" />
                Research-backed
              </a>
              {" "}· No signup
            </span>
          </div>
        </>
      )}
    </form>
  );
};

export default ScanForm;
